/*!
 * parserSignalRef.js — link() bindings and if: conditions
 * Part of oscillaScore control plane architecture
 * © 2025 Rob Canning — GPLv3
 *
 * A binding says "this parameter follows that signal":
 *
 *   synth(freq: link(fader1.t, 200, 2000))
 *
 * ── SYNTAX ─────────────────────────────────────────────────────────────────
 *   link(source.channel)                     0-1 straight through
 *   link(source.channel, min, max)           mapped into a range
 *   link(source.channel, min, max, curve)    curve: lin exp2 exp3 exp4 log sqrt
 *                                            (exp is exp2; control/curves.js)
 *   link(source.channel, min, max, default)  value to hold until the first
 *                                            signal arrives
 *   link(source.channel, min, max, default, curve)
 *   link(/osc/address, min, max)             an incoming OSC address —
 *                                            a leading slash is what marks it
 *
 * The 4th argument is read by type: a number is a default, a name is a curve.
 * Because the arguments are separated rather than run together, that is
 * unambiguous to a reader as well as to the parser.
 *
 * ── WHY A WRAPPER ──────────────────────────────────────────────────────────
 * link() needs no new grammar: nested calls in value position already work
 * (Prand(0.5, 1, 2, inf), midi(60)), and Identifier already spans dots, so the
 * existing simpleFuncCall rule parses it. And `clip.webm` and `fader1.t` are
 * indistinguishable as bare strings; an explicit call says it is a binding
 * itself, on any parameter, with no allowlist of parameter names.
 */

import { CURVE_NAMES } from '../control/curves.js';

/**
 * The call names that mark a binding.
 *
 * follow() says what the construct actually does: this parameter TRACKS that
 * signal, continuously, for as long as both exist. link() still works and
 * always will — there are scores carrying it — but follow() is what the docs
 * and the editor offer.
 *
 * The distinction is worth the rename: fade(from:, to:) samples its endpoints
 * ONCE, at trigger. follow() never stops reading. A verb makes that difference
 * visible at a glance, where a noun hid it.
 */
export const FOLLOW_FN = 'follow';
export const LINK_FN   = 'link';        // the original spelling, still accepted
export const MAP_FN    = 'map';

const BINDING_FNS = new Set([FOLLOW_FN, LINK_FN]);

/** Is this AST value a follow() — or a legacy link() — call? */
export function isFollowCall(value) {
  return !!value && typeof value === 'object'
    && value.type === 'funcCall'
    && BINDING_FNS.has(String(value.name).toLowerCase());
}

/** @deprecated the original name for isFollowCall(); kept for importers. */
export const isLinkCall = isFollowCall;

export function isMapCall(value) {
  return !!value && typeof value === 'object'
    && value.type === 'funcCall'
    && String(value.name).toLowerCase() === MAP_FN;
}

export function isSignalRef(value) {
  return !!value && typeof value === 'object' && value.type === 'signalRef';
}

export function isOscRef(value) {
  return !!value && typeof value === 'object' && value.type === 'oscRef';
}

export function isCurveName(name) {
  return typeof name === 'string' && CURVE_NAMES.has(name.toLowerCase());
}

/**
 * Turn a parsed follow() (or legacy link()) call into a signalRef or an oscRef.
 *
 * @param {object} call - { type:'funcCall', name:'follow'|'link', args:[…] }
 * @returns {object|null} signalRef / oscRef, or null if the call is malformed
 *                        (the caller keeps the original value and the load-time
 *                        reference report explains what went wrong)
 */
export function parseFollowCall(call) {
  if (!isFollowCall(call)) return null;
  const args = Array.isArray(call.args) ? call.args : [];
  if (!args.length) {
    console.warn('[follow] needs a source: follow(fader1.t) or follow(/osc/address, min, max)');
    return null;
  }

  const source = args[0];
  if (typeof source !== 'string' || !source) {
    console.warn('[follow] first argument must be a source, got:', source);
    return null;
  }

  // Range is positional and must be numeric on both sides — a half-given range
  // is a mistake worth naming rather than silently half-applying.
  const rest = args.slice(1);
  let range;
  if (rest.length >= 2 && typeof rest[0] === 'number' && typeof rest[1] === 'number') {
    range = [rest[0], rest[1]];
  } else if (rest.length) {
    console.warn(`[follow] ignoring arguments after "${source}" — expected link(${source}, min, max, …)`);
  }

  const ref = source.startsWith('/')
    ? { type: 'oscRef', address: source }                 // leading slash ⇒ OSC
    : buildSignalRef(source);
  if (!ref) return null;
  if (range) ref.range = range;

  // Tail arguments, read by type: number ⇒ default, name ⇒ curve.
  for (const extra of rest.slice(range ? 2 : 0)) {
    if (typeof extra === 'number') ref.default = extra;
    else if (isCurveName(extra)) ref.curve = String(extra).toLowerCase();
    else if (extra != null) console.warn(`[follow] unknown argument "${extra}" in follow(${source}, …)`);
  }

  return ref;
}

function buildSignalRef(source) {
  const dot = source.indexOf('.');
  if (dot <= 0 || dot === source.length - 1) {
    console.warn(`[follow] source must be <uid>.<channel>, got "${source}"`);
    return null;
  }
  return {
    type: 'signalRef',
    source: source.slice(0, dot),
    channel: source.slice(dot + 1),
  };
}

/**
 * Arithmetic on signals: mul(a, b) and add(a, b).
 *
 *   sx: mul(follow(adc.amp, 0, 2), follow(lfo.norm, 0.5, 1))
 *   sx: add(follow(a.t, 0, 1), 0.25)
 *
 * Each TERM carries its own range, because a term is just a follow() or map()
 * — the thing that already knows how to read a source. That is what makes this
 * compose: anything you could bind to a parameter, you can now multiply by
 * anything else you could bind to a parameter, and a plain number is a term
 * too. The alternative (bare sources plus one outer range) is terser for the
 * simplest case and a dead end for every other.
 *
 * Neither name needed a grammar change. simpleFuncCall gates on "identifier
 * followed by (", so these arrive as ordinary nested funcCalls — and `add` as
 * a CALL does not collide with speed(add:), which is a parameter key.
 */
const COMBINE_FNS = new Set(['mul', 'add']);

export function isCombineCall(value) {
  return !!value && typeof value === 'object'
    && value.type === 'funcCall'
    && COMBINE_FNS.has(String(value.name).toLowerCase());
}

/**
 * Turn mul(...) / add(...) into a combine node.
 *
 * @returns {object|null} { type:'combine', op, terms:[signalRef|oscRef|number] }
 *                        or null if no term names a source — arithmetic on two
 *                        constants is a number, not a binding, and quietly
 *                        creating a binding that can never change would be
 *                        worse than leaving the value alone.
 */
export function parseCombineCall(call) {
  if (!isCombineCall(call)) return null;
  const op = String(call.name).toLowerCase();
  const args = Array.isArray(call.args) ? call.args : [];
  if (args.length < 2) {
    console.warn(`[${op}] needs at least two terms: ${op}(follow(a.t), follow(b.t))`);
    return null;
  }

  const terms = [];
  for (const a of args) {
    if (typeof a === 'number') { terms.push(a); continue; }
    // A nested follow()/map() is the usual term; an already-converted ref is
    // accepted too, so order of conversion never matters.
    const ref = isSignalRef(a) || isOscRef(a) ? a
              : isMapCall(a)                  ? parseMapCall(a)
              : isFollowCall(a)               ? parseFollowCall(a)
              : isCombineCall(a)              ? parseCombineCall(a)   // nesting
              : null;
    if (!ref) {
      console.warn(`[${op}] a term must be follow(), map(), ${op}() or a number — got ` +
                   (a && a.type ? a.type : JSON.stringify(a)));
      return null;
    }
    terms.push(ref);
  }

  if (!terms.some((t) => typeof t !== 'number')) {
    console.warn(`[${op}] every term is a constant — this is a number, not a binding`);
    return null;
  }
  return { type: 'combine', op, terms };
}

/** Is this a parsed combine node (as opposed to the call that produced it)? */
export function isCombine(value) {
  return !!value && typeof value === 'object' && value.type === 'combine';
}

const HYPHEN_REF_PATTERN = /^([a-zA-Z_][a-zA-Z0-9_]*)\.([a-zA-Z_][a-zA-Z0-9_]*)(.*)$/;

/**
 * The original binding spelling: `fader1.t-0-127`, source.channel then a
 * hyphenated range, optional default, optional curve.
 *
 * Real scores in ~/oscilla-projects still use it.
 *
 * A RANGE IS REQUIRED, which is the whole trick. Bare `a.b` strings are
 * filenames and function references — lum.mp4, hit.wav, p5sketch.setup,
 * confetti.onClick. None of them carries `-min-max`, so requiring the range
 * separates the two cleanly.
 *
 * Deprecated, not revived: follow() is the spelling to write. This exists so
 * scores written before it keep playing.
 */
export function parseSignalRef(str) {
  if (!str || typeof str !== 'string') return null;
  const match = str.match(HYPHEN_REF_PATTERN);
  if (!match) return null;

  const [, source, channel, rest] = match;
  if (!rest) return null;                 // bare source.channel — a filename, not a binding

  // Split on hyphens, keeping those that belong to a negative number.
  const segments = [];
  let current = '';
  let inNumber = false;
  for (let i = 1; i < rest.length; i++) {
    const char = rest[i];
    if (char === '-') {
      if (current === '' || (inNumber && /^-?[\d.]+e$/i.test(current))) {
        current += char; inNumber = true;
      } else {
        if (current) segments.push(current);
        current = ''; inNumber = false;
      }
    } else {
      current += char;
      if (/[\d.]/.test(char)) inNumber = true;
    }
  }
  if (current) segments.push(current);

  if (segments.length < 2) return null;
  const min = parseFloat(segments[0]);
  const max = parseFloat(segments[1]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

  const ref = { type: 'signalRef', source, channel, range: [min, max] };

  // Trailing segments read by type, as follow() does: a number is the default,
  // a known name is the curve.
  for (const seg of segments.slice(2)) {
    if (isCurveName(seg)) ref.curve = String(seg).toLowerCase();
    else if (Number.isFinite(parseFloat(seg))) ref.default = parseFloat(seg);
  }
  return ref;
}

/**
 * Convert a value to a binding if it is a follow(), map() or mul()/add() call,
 * or a string in the hyphen spelling (a.b-0-1, parseSignalRef); otherwise
 * return it untouched. Called on every extracted parameter value, with no
 * allowlist of parameter names.
 */
export function maybeConvertBinding(value) {
  if (isCombineCall(value)) return parseCombineCall(value) ?? value;
  if (isMapCall(value)) return parseMapCall(value) ?? value;
  // The pre-follow() spelling, kept for scores already written in it. Only the
  // ranged form is recognised — see parseSignalRef.
  if (typeof value === 'string') return parseSignalRef(value) ?? value;
  if (!isFollowCall(value)) return value;
  return parseFollowCall(value) ?? value;
}

/**
 * Every source a binding reads, flattened — one entry per cord.
 * A plain follow() has one; mul(a, b) has two; nesting goes as deep as it goes.
 * The edge builder and the reference check both walk this, so a combined
 * binding draws a cord per term and reports a broken term like any other.
 */
export function bindingSources(value) {
  if (isSignalRef(value) || isOscRef(value)) return [value];
  if (isCombine(value)) return value.terms.flatMap(bindingSources);
  return [];
}

/** @deprecated the original names; kept so existing importers keep working. */
export const parseLinkCall = parseFollowCall;
export const maybeConvertLink = maybeConvertBinding;

/**
 * map(source, inMin, inMax, outMin, outMax [, curve])
 *
 * link() with the input range spelled out. link(fx.t, 90, 2000) assumes the
 * source sweeps 0-1; map() says which part of it to use, so a slow or
 * narrow-ranging source can still drive a parameter across its whole span:
 *
 *   freq: link(fx.t, 90, 2000)              # 0-1  -> 90..2000
 *   freq: map(fx.t, 0, 1, 90, 2000)         # the same thing, written out
 *   freq: map(fx.t, 0.3, 0.35, 90, 2000)    # a 5% slice -> the full range
 *
 * Input outside the window pins to the nearer end (applyCurve clamps), so a
 * window is a window and not an extrapolation.
 *
 * Both forms produce the same signalRef; map just also carries inRange, which
 * is why everything downstream — bindings, the edge graph, the connections
 * view — treats the two identically.
 */
export function parseMapCall(call) {
  if (!isMapCall(call)) return null;
  const args = Array.isArray(call.args) ? call.args : [];
  const source = args[0];
  if (typeof source !== 'string' || !source) {
    console.warn('[map] first argument must be a source: map(fader1.t, 0, 1, 90, 2000)');
    return null;
  }

  const nums = args.slice(1, 5);
  if (nums.length < 4 || !nums.every(n => typeof n === 'number')) {
    console.warn(`[map] needs four numbers: map(${source}, inMin, inMax, outMin, outMax [, curve])`);
    return null;
  }
  const [inMin, inMax, outMin, outMax] = nums;
  if (inMin === inMax) {
    console.warn(`[map] inMin and inMax are both ${inMin} — the window has no width, so nothing would move`);
    return null;
  }

  const ref = source.startsWith('/')
    ? { type: 'oscRef', address: source }
    : buildSignalRef(source);
  if (!ref) return null;

  ref.range = [outMin, outMax];
  ref.inRange = [inMin, inMax];

  for (const extra of args.slice(5)) {
    if (typeof extra === 'number') ref.default = extra;
    else if (isCurveName(extra)) ref.curve = String(extra).toLowerCase();
    else if (extra != null) console.warn(`[map] unknown argument "${extra}" in map(${source}, …)`);
  }
  return ref;
}

// ===========================
// Conditional Cue Triggering
// ===========================

/**
 * Condition syntax for the `if:` parameter
 *
 *   signal>value    - greater than
 *   signal>=value   - greater or equal
 *   signal<value    - less than
 *   signal<=value   - less or equal
 *   signal==value   - equal
 *   signal!=value   - not equal
 *   signal          - truthy check (> 0)
 *
 * Signal paths:
 *   mic.amp                 - audio analysis
 *   fader1.t                - o2p fader position
 *   source:id.channel       - full form with type prefix (e.g., o2p:slider.t)
 */
const CONDITION_PATTERN = /^([a-zA-Z_][a-zA-Z0-9_:.-]*)\s*(>=|<=|==|!=|>|<)?\s*(-?[0-9]*\.?[0-9]+)?$/;

/**
 * Check if a string looks like a condition expression
 * Must contain a signal path (with dot or colon) and optionally a comparison
 * @param {string} str - String to check
 * @returns {boolean}
 */
export function looksLikeCondition(str) {
  if (!str || typeof str !== 'string') return false;
  const trimmed = str.trim();
  // Must contain a dot or colon (signal path indicator) and match pattern
  if (!trimmed.includes('.') && !trimmed.includes(':')) return false;
  return CONDITION_PATTERN.test(trimmed);
}

/**
 * Parse a condition string into a condition object
 * @param {string} str - String like "mic.amp>0.7" or "fader1.t>=0.5" or "mic.onset"
 * @returns {Object|null} Condition object or null if not valid
 *
 * Returns: {
 *   type: 'condition',
 *   signal: 'mic.amp',        // signal path for paramBus lookup
 *   op: '>',                  // comparison operator (null for truthy check)
 *   threshold: 0.7            // comparison value (null for truthy check)
 * }
 */
export function parseCondition(str) {
  if (!str || typeof str !== 'string') return null;

  const trimmed = str.trim();
  const match = trimmed.match(CONDITION_PATTERN);
  if (!match) return null;

  const [, signal, op, thresholdStr] = match;

  const condition = {
    type: 'condition',
    signal
  };

  if (op && thresholdStr !== undefined) {
    condition.op = op;
    condition.threshold = parseFloat(thresholdStr);
  } else {
    // No operator = truthy check (signal > 0)
    condition.op = null;
    condition.threshold = null;
  }

  return condition;
}

/**
 * Check if a value is a parsed condition object
 * @param {any} value - Value to check
 * @returns {boolean}
 */
export function isCondition(value) {
  return value && typeof value === 'object' && value.type === 'condition';
}

/**
 * Convert a string value to a condition if it looks like one
 * Used during AST extraction for the 'if' parameter
 * @param {any} value - Raw value from parser
 * @returns {any} Condition object or original value
 */
export function maybeConvertToCondition(value) {
  if (typeof value === 'string' && looksLikeCondition(value)) {
    const condition = parseCondition(value);
    if (condition) {
      return condition;
    }
  }
  return value;
}

